import React, { memo, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import cn from 'classnames';

import { ChipsSelectMenuPlacement } from '@components/common';
import ValueContent from '@components/common/GroupSelectKpi/ValueContent';

import { isEmpty } from '@utils/common';

import {
	IDefaultOptions, IGroupSelectItem,
	IGroupSelectOptions,
	IGroupSelectOptionsKpi,
	IGroupSelectProps,
} from './GroupSelect.types';

import Meta from '../Meta';
import Modal from '../Modal';
import GroupSelectMenu from './GroupSelectMenu';
import {
	generateDefaultValues,
	generateDynamicOptions,
	generateListOfCheckedOptions,
	generateListOfCheckedOptionsWithCategory,
	generateOptions,
	generateOptionsByIds,
} from './groupSelectUtils';

import '../GroupSelect/GroupSelect.scss';

export interface IGroupSelectKpiProps extends IGroupSelectProps {
	kpiValues: IGroupSelectOptions[];
}

const GroupSelectKpi: React.FC<IGroupSelectKpiProps> = ({
	disabled,
	errorMessage,
	gap = '20px',
	iSortByChecked = true,
	isCheckboxTheme = false,
	isInLine = false,
	kpiValues,
	label,
	maxSelectionNumber,
	maxSelectionText,
	menuPlacement = ChipsSelectMenuPlacement.topEnd,
	modalLabel,
	onChange,
	options = [],
	readOnly,
	required = false,
	searchPlaceholder,
	searchThreshold,
	selectAllName = 'all',
	setSelectedItemsNumber,
	withVirtualization = false,
}) => {
	const countRef = useRef(0);

	const containerRef = useRef<HTMLDivElement>(null);

	const generatedDefaultValuesKpi: IDefaultOptions[] = useMemo(() => {
		return !isEmpty(kpiValues) ? generateDefaultValues(kpiValues) : [];
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	const [defaultValueKpi, setDefaultValueKpi] = useState<IDefaultOptions[]>(
		generatedDefaultValuesKpi,
	);

	const [isAllItemsSelected, setIsAllItemsSelected] = useState(false);
	const [isSelectMenuOpen, setIsSelectMenuOpen] = useState(false);

	const defaultDynamicOpt: IGroupSelectOptions[] = useMemo(() => {
		return generateDynamicOptions(options, defaultValueKpi);
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	const [dynamicOptions, setDynamicOptions] = useState<IGroupSelectOptions[]>(defaultDynamicOpt);

	const getSelectedListOfOptions = useCallback(() => generateListOfCheckedOptions(dynamicOptions), [dynamicOptions]);

	const defaultSelectedListOfOpt = useMemo(() => {
		return getSelectedListOfOptions();
	}, [getSelectedListOfOptions]);

	const [selectedListOfOptions, setSelectedListOfOptions] = useState(defaultSelectedListOfOpt);

	useEffect(() => {
		if (!isEmpty(kpiValues)) {
			setDefaultValueKpi(generateDefaultValues(kpiValues)!);
			setSelectedListOfOptions(getSelectedListOfOptions());
		} else {
			setDefaultValueKpi(generateDefaultValues([]));
			setSelectedListOfOptions([]);
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [kpiValues]);

	useEffect(() => {
		const dynamicOpt = generateDynamicOptions(options, defaultValueKpi);

		setDynamicOptions(dynamicOpt);
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [options.length, defaultValueKpi]);

	const countAllOptions: number = useMemo(() => {
			return options.reduce<number>((acc: number, option: IGroupSelectOptionsKpi) => {
				const itemCountInCategory: number = option.categoryGroups.reduce((acc, categoryGroup) => {
					acc = acc + categoryGroup.items.length;

					return acc;
				}, 0);

				return acc + itemCountInCategory;
			}, 0);
		},
		[options],
	);

	const isDisableOpenSelectMenu = !dynamicOptions.length || disabled;

	const onChangeHandler = useCallback((options: Array<IGroupSelectOptions>) => {
		const checkedList: Array<IGroupSelectOptions> = generateListOfCheckedOptionsWithCategory(options);

		onChange(checkedList);
	}, [onChange]);

	const removeAllOptions = useCallback(() => {
		const uncheckedOptions = generateOptions(options, false);

		onChangeHandler(uncheckedOptions);
	}, [onChangeHandler, options]);

	const removeOptions = useCallback((ids: Array<string>, categoryNames: string[]) => {
		const generatedOptions = generateOptionsByIds(dynamicOptions, false, ids, categoryNames);

		onChangeHandler(generatedOptions);
	}, [dynamicOptions, onChangeHandler]);

	useEffect(() => {
		const newSelectedOptions: IGroupSelectItem[] = getSelectedListOfOptions();

		setSelectedListOfOptions(newSelectedOptions);
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [dynamicOptions]);

	useEffect(() => {
		setIsAllItemsSelected(countAllOptions === selectedListOfOptions.length);

		if (setSelectedItemsNumber) setSelectedItemsNumber(selectedListOfOptions.length);
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [dynamicOptions, selectedListOfOptions]);

	const toggleSelectMenu = () => {
		if (isDisableOpenSelectMenu) return;

		setIsSelectMenuOpen((prevState) => !prevState);
	};

	countRef.current = countRef.current + 1;

	return (
		<div
			ref={containerRef}
			className={cn('f5gGroupSelectContainer', {
				f5gGroupSelectContainerMinWidth: !isCheckboxTheme,
				f5gGroupSelectContainerInLine: isCheckboxTheme && isInLine,
			})}
			data-testid='group-select'
			style={{
				gap,
			}}
		>
			<ValueContent
				containerRef={containerRef}
				isAllItemsSelected={isAllItemsSelected}
				menuPlacement={menuPlacement}
				readOnly={readOnly}
				removeAllOptions={removeAllOptions}
				removeOptions={removeOptions}
				selectedListOfOptions={selectedListOfOptions || []}
			/>
			<div className='f5gGroupSelectControl'>
				<div
					className={cn({ f5gGroupSelectRequired: required && !!selectedListOfOptions.length })}
					data-testid='group-select-button'
					onClick={toggleSelectMenu}
				>
					<span
						className={cn('f5gGroupSelectButton', {
							f5gGroupSelectButtonDisabled: isDisableOpenSelectMenu,
							f5gGroupSelectButtonReadOnly: readOnly,
						})}
					>
						{label}
					</span>
				</div>
				&nbsp;
				{isCheckboxTheme && !!selectedListOfOptions.length && (
					<div>({isAllItemsSelected ? 'all' : selectedListOfOptions.length})</div>
				)}
			</div>
			{errorMessage && <Meta message={errorMessage} />}
			<Modal
				isOpen={isSelectMenuOpen}
				onClose={toggleSelectMenu}
				title={modalLabel || label}
				width='650px'
			>
				<GroupSelectMenu
					defaultOptions={options}
					dynamicOptions={dynamicOptions}
					iSortByChecked={iSortByChecked}
					isAllItemsSelected={isAllItemsSelected}
					maxSelectionNumber={maxSelectionNumber}
					maxSelectionText={maxSelectionText}
					onSave={onChangeHandler}
					placeholder={searchPlaceholder}
					readOnly={readOnly}
					searchThreshold={searchThreshold}
					selectAllName={selectAllName}
					toggleSelectMenu={toggleSelectMenu}
					withVirtualization={withVirtualization}
				/>
			</Modal>
		</div>
	);
};

export default memo(GroupSelectKpi);
